// ============================================
// FAMUZA MARKET — Cart / Basket Page
// ============================================

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';

const CartIcon = ({ size = 22, color = 'rgba(245,240,232,0.9)' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1"/>
    <circle cx="20" cy="21" r="1"/>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
  </svg>
);

const WaIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.558 4.118 1.528 5.845L0 24l6.335-1.508A11.953 11.953 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.006-1.366l-.36-.213-3.724.977.994-3.634-.234-.374A9.818 9.818 0 1112 21.818z"/>
  </svg>
);

export default function Cart() {
  const { cart, removeFromCart, updateQty, formatPrice, clearCart } = useApp();
  const navigate = useNavigate();

  const total = cart ? cart.reduce((sum, item) => sum + (item.price * (item.qty || 1)), 0) : 0;
  const totalItems = cart ? cart.reduce((sum, item) => sum + (item.qty || 1), 0) : 0;

  // Group items by seller for WhatsApp checkout
  const bySeller = cart ? cart.reduce((acc, item) => {
    const key = item.sellerPhone || '260970000000';
    if (!acc[key]) acc[key] = { phone: key, name: item.sellerName || 'FAMUZA Seller', items: [] };
    acc[key].items.push(item);
    return acc;
  }, {}) : {};

  const handleCheckoutAll = () => {
    if (!cart || cart.length === 0) return;
    const itemsList = cart.map(item => `• ${item.name} x${item.qty || 1} — ${formatPrice(item.price * (item.qty || 1))}`).join('\n');
    const msg = `Hi! I'd like to order the following items from FAMUZA Market:\n\n${itemsList}\n\n*Total: ${formatPrice(total)}*\n\nPlease let me know how to arrange payment and delivery. Thank you!`;
    window.open(`https://wa.me/260970000000?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleCheckoutSeller = (seller) => {
    const itemsList = seller.items.map(item => `• ${item.name} x${item.qty || 1} — ${formatPrice(item.price * (item.qty || 1))}`).join('\n');
    const sellerTotal = seller.items.reduce((sum, item) => sum + (item.price * (item.qty || 1)), 0);
    const msg = `Hi! I'd like to order the following from FAMUZA Market:\n\n${itemsList}\n\n*Total: ${formatPrice(sellerTotal)}*\n\nPlease let me know how to arrange payment and delivery. Thank you!`;
    window.open(`https://wa.me/${seller.phone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <div style={{ background: '#0D0D0D', minHeight: '100vh' }}>

      {/* Nav */}
      <div style={{ background: '#161616', borderBottom: '0.5px solid rgba(255,255,255,0.07)', padding: '0.875rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.1rem', fontWeight: 800, color: '#F5F0E8' }}>
            FAMUZA <span style={{ color: '#E8622A' }}>Market</span>
          </div>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <CartIcon size={20} />
          <span style={{ fontSize: '0.85rem', color: 'rgba(245,240,232,0.6)', fontFamily: 'DM Sans, sans-serif' }}>
            {totalItems} item{totalItems !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      <div style={{ maxWidth: 700, margin: '0 auto', padding: '1.5rem 1rem' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.6rem', fontWeight: 800, color: '#F5F0E8' }}>
            My <span style={{ color: '#E8622A' }}>Basket</span>
          </h1>
          {cart && cart.length > 0 && (
            <button onClick={() => { if (window.confirm('Clear all items from basket?')) clearCart(); }}
              style={{ background: 'transparent', border: 'none', color: 'rgba(245,240,232,0.35)', fontSize: '0.78rem', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer', textDecoration: 'underline' }}>
              Clear all
            </button>
          )}
        </div>

        {/* Empty state */}
        {(!cart || cart.length === 0) && (
          <div style={{ textAlign: 'center', padding: '4rem 1rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🛒</div>
            <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.2rem', fontWeight: 800, color: '#F5F0E8', marginBottom: '0.5rem' }}>Your basket is empty</div>
            <div style={{ fontSize: '0.85rem', color: 'rgba(245,240,232,0.4)', marginBottom: '1.5rem' }}>Browse listings and add items you like!</div>
            <button onClick={() => navigate('/listings')}
              style={{ background: '#E8622A', color: '#fff', border: 'none', borderRadius: 100, padding: '0.875rem 2rem', fontSize: '0.95rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, cursor: 'pointer' }}>
              Browse Listings
            </button>
          </div>
        )}

        {/* Cart items */}
        {cart && cart.length > 0 && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {cart.map(item => (
                <div key={item.id} style={{ background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: '0.875rem', display: 'flex', gap: '0.875rem', alignItems: 'center' }}>
                  <div onClick={() => navigate(`/product/${item.id}`)}
                    style={{ width: 75, height: 75, borderRadius: 10, overflow: 'hidden', background: '#0D0D0D', flexShrink: 0, cursor: 'pointer' }}>
                    <img src={item.image} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div onClick={() => navigate(`/product/${item.id}`)}
                      style={{ fontSize: '0.88rem', fontWeight: 600, color: '#F5F0E8', marginBottom: 4, lineHeight: 1.3, cursor: 'pointer' }}>
                      {item.name}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'rgba(245,240,232,0.4)', marginBottom: 8 }}>
                      {item.sellerName || 'FAMUZA Seller'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1rem', fontWeight: 800, color: '#8B6914' }}>
                        {formatPrice(item.price * (item.qty || 1))}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <button onClick={() => updateQty(item.id, (item.qty || 1) - 1)}
                          style={{ width: 28, height: 28, borderRadius: '50%', background: '#2A2A2A', border: 'none', color: '#F5F0E8', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                        <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#F5F0E8', minWidth: 20, textAlign: 'center' }}>{item.qty || 1}</span>
                        <button onClick={() => updateQty(item.id, (item.qty || 1) + 1)}
                          style={{ width: 28, height: 28, borderRadius: '50%', background: '#2A2A2A', border: 'none', color: '#F5F0E8', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                        <button onClick={() => removeFromCart(item.id)}
                          style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(232,98,42,0.1)', border: '0.5px solid rgba(232,98,42,0.3)', color: '#E8622A', fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Order summary */}
            <div style={{ background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: '1.25rem', marginBottom: '1.25rem' }}>
              <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1rem', fontWeight: 700, color: '#F5F0E8', marginBottom: '1rem' }}>Order Summary</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                <span style={{ color: 'rgba(245,240,232,0.5)' }}>Items ({totalItems})</span>
                <span style={{ color: '#F5F0E8' }}>{formatPrice(total)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                <span style={{ color: 'rgba(245,240,232,0.5)' }}>Delivery</span>
                <span style={{ color: '#25D366' }}>Negotiate with seller</span>
              </div>
              <div style={{ height: '0.5px', background: 'rgba(255,255,255,0.07)', margin: '0.75rem 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.1rem' }}>
                <span style={{ fontWeight: 700, color: '#F5F0E8' }}>Total</span>
                <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, color: '#8B6914' }}>{formatPrice(total)}</span>
              </div>
            </div>

            {/* Checkout by seller — if multiple sellers */}
            {Object.keys(bySeller).length > 1 && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.78rem', color: 'rgba(245,240,232,0.4)', marginBottom: '0.75rem', fontFamily: 'DM Sans, sans-serif' }}>
                  💡 Your basket has items from {Object.keys(bySeller).length} sellers. Message each seller separately or send all at once.
                </div>
                {Object.values(bySeller).map(seller => (
                  <button key={seller.phone} onClick={() => handleCheckoutSeller(seller)}
                    style={{ width: '100%', padding: '0.75rem', background: 'rgba(37,211,102,0.08)', border: '0.5px solid rgba(37,211,102,0.2)', color: '#25D366', borderRadius: 12, fontSize: '0.85rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, cursor: 'pointer', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                    <WaIcon size={15} /> Message {seller.name} ({seller.items.length} item{seller.items.length !== 1 ? 's' : ''})
                  </button>
                ))}
              </div>
            )}

            {/* Main checkout button */}
            <button onClick={handleCheckoutAll}
              style={{ width: '100%', padding: '1rem', background: '#25D366', color: '#fff', border: 'none', borderRadius: 14, fontSize: '1.05rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem', boxShadow: '0 4px 20px rgba(37,211,102,0.35)', marginBottom: '0.75rem' }}>
              <WaIcon size={20} /> Checkout via WhatsApp
            </button>

            {/* Continue Shopping — fixed with navigate */}
            <button onClick={() => navigate('/listings')}
              style={{ width: '100%', padding: '0.875rem', background: 'transparent', color: 'rgba(245,240,232,0.6)', border: '0.5px solid rgba(255,255,255,0.15)', borderRadius: 14, fontSize: '0.95rem', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer' }}>
              ← Continue Shopping
            </button>

            {/* Payment methods */}
            <div style={{ marginTop: '1.25rem', background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '0.875rem', textAlign: 'center' }}>
              <div style={{ fontSize: '0.72rem', color: 'rgba(245,240,232,0.4)', marginBottom: '0.5rem' }}>Accepted payment methods</div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                {['📱 MTN MoMo', '📡 Airtel Money', '💵 Cash on Delivery'].map(m => (
                  <span key={m} style={{ background: '#1E1E1E', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 8, padding: '4px 10px', fontSize: '0.75rem', color: 'rgba(245,240,232,0.6)' }}>{m}</span>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
